import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {IPlayer, IPlayersStore} from "data/stores/players/players.store";
import {Bindings} from "data/constants/bindings";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import type {IFantasyStore} from "data/stores/fantasy/fantasy.store";

interface IParam {
	player: IPlayer;
}

export interface IPlayerCardController extends ViewController<IParam> {
	get squad(): ISquad | undefined;
	get nextOpponent(): ISquad | undefined;
	get displayStat(): string;
	get isSelected(): boolean;
	selectPlayer: () => void;
}

@injectable()
export class PlayerCardController implements IPlayerCardController {
	@observable private _player: IPlayer | undefined;

	get squad() {
		if (!this._player) {
			return;
		}
		return this._squadsStore.getSquadById(this._player.squadId);
	}

	get nextOpponent() {
		if (!this._player) {
			return;
		}

		return this._squadsStore.getNextOpponent(this._player.squadId);
	}

	get displayStat() {
		return `vs ${this.nextOpponent?.abbreviation ?? "-"}`;
	}

	get isSelected() {
		if (!this._player) {
			return false;
		}
		return this._player.id === this._fantasyStore.selectedPlayerId;
	}

	constructor(
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.FantasyStore) private _fantasyStore: IFantasyStore
	) {
		makeAutoObservable(this);
	}

	@action selectPlayer = () => {
		if (!this._player) {
			return;
		}
		this._fantasyStore.selectedPlayerId = this._player.id;
	};

	@action init(param: IParam): void {
		this._player = param.player;
	}

	@action onChange(param: IParam): void {
		this._player = param.player;
	}
}
